/**
 * "Delete these versions?" — the ONE confirm dialog for every retention-lowering Save (#1443): folder rules, the
 * org-wide limits (extension, kind, ceiling, app ceiling), an app's own limit and a file's own limit.
 *
 * Its numbers are the purge's own decision (the server's `previewRetention`), so they say exactly what Save deletes —
 * unless `exact` is false (too many files to ask each one), when the copy says "at most" and that it may be fewer.
 * Each file's deciding rule is named (#1442): a `.pyc` going 6 → 1 under the built-in `__pycache__` rule must not read
 * as the change being saved.
 *
 * Deleted versions cannot be restored, so nothing here deletes: `onConfirm` is the caller's Save with `purge: true`.
 */
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { formatStorageBytes } from '@papan/types'
import { Modal } from './Modal'

/** The ladder layer that decided (`file`, `folder`, `ext`, `kind`, `tenant`, `platform`, `shipped`, `requested`), or null. */
export type RetentionRule = string | null

/** The preview shape the server answers (folder-rule samples carry `versionCount → keep` for older clients). */
export interface RetentionPurgePreviewView {
  willPurge: boolean
  files: number
  versions: number
  bytes: number
  totalBytes: number
  totalVersions?: number
  appVersions?: number
  exact?: boolean
  truncated: boolean
  samples: Array<{
    relPath: string
    bytes: number
    totalBytes: number
    rule?: RetentionRule
    folderRule?: string
    builtIn?: boolean
    // either shape: `before → after` (the shared preview) or `versionCount → keep` (the folder-rule route's)
    before?: number
    after?: number
    versionCount?: number
    keep?: number
  }>
  byRule?: Array<{ rule: RetentionRule; folderRule?: string; builtIn?: boolean; files: number; versions: number; bytes: number }>
}

export interface RetentionPurgeDialogProps {
  preview: RetentionPurgePreviewView
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
  /** Test-id prefix (the folder-rules tab keeps its historical ids). */
  testId?: string
  formatBytes?: (n: number) => string
}

export function RetentionPurgeDialog({
  preview, busy, onCancel, onConfirm, testId = 'retention-purge', formatBytes = formatStorageBytes,
}: RetentionPurgeDialogProps) {
  const { t } = useTranslation('core-ui')
  const k = (key: string, opts?: Record<string, unknown>): string => String(t(`settings.folderRules.purge.${key}`, (opts ?? {}) as Record<string, unknown>))
  const ruleLabel = (r: { rule: RetentionRule; folderRule?: string; builtIn?: boolean }): string => {
    if (r.rule === 'folder' && r.folderRule) return k(r.builtIn ? 'rule.folderBuiltIn' : 'rule.folder', { pattern: r.folderRule })
    return k(`rule.${r.rule ?? 'unknown'}`)
  }
  const exact = preview.exact !== false
  const fileLabel = k('files', { count: preview.files })
  return (
    <Modal
      title={k('title')}
      onClose={onCancel}
      maxWidth="max-w-lg"
      footer={(
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`${testId}-cancel`}
            onClick={onCancel}
            disabled={busy}
            className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
          >
            {k('cancel')}
          </button>
          <button
            type="button"
            data-testid={`${testId}-confirm`}
            onClick={onConfirm}
            disabled={busy}
            className="px-2.5 py-1 text-xs rounded bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
          >
            {busy ? t('settings.folderRules.saving') : k('confirm')}
          </button>
        </div>
      )}
    >
      <div data-testid={testId} className="px-5 py-4 flex flex-col gap-3 text-xs">
        {/* Too big to count: say so rather than show a confident zero for a purge that will still delete plenty. */}
        {preview.truncated ? (
          <p className="themed-text">{k('unknown')}</p>
        ) : (
          <>
            <p className="themed-text" data-testid={`${testId}-body`}>
              {/* two counts in one sentence: the file half is its own pluralised fragment */}
              {k(exact ? 'bodyExact' : 'body', { versions: preview.versions, count: preview.versions, fileLabel })}
              {preview.appVersions ? ` ${k('appVersions', { count: preview.appVersions })}` : ''}
            </p>
            {!exact && <p className="themed-text-muted" data-testid={`${testId}-estimate`}>{k('estimate')}</p>}

            {/* BEFORE AND AFTER on one scale: the gap between the two bars IS the saving. */}
            {preview.totalBytes > 0 && (
              <div data-testid={`${testId}-bar`} className="flex flex-col gap-1.5">
                <div className="themed-text-muted">{k('storageLabel')}</div>
                {([
                  { key: 'now', bytes: preview.totalBytes },
                  { key: 'after', bytes: preview.totalBytes - preview.bytes },
                ] as const).map((row) => (
                  <div key={row.key} className="flex items-center gap-2">
                    <span className="themed-text-muted w-10 shrink-0">{k(row.key)}</span>
                    <div aria-hidden="true" className="flex-1 h-2 rounded-full themed-bg-secondary overflow-hidden">
                      <div
                        data-testid={`${testId}-bar-${row.key}`}
                        className={`h-full rounded-full ${row.key === 'now' ? 'bg-zinc-500' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.round((row.bytes / preview.totalBytes) * 100)}%` }}
                      />
                    </div>
                    <span className="tabular-nums shrink-0 themed-text w-20 text-right">{formatBytes(row.bytes)}</span>
                  </div>
                ))}
                <div className="text-emerald-400 text-right">{k('frees', { size: formatBytes(preview.bytes) })}</div>
              </div>
            )}

            {/* Which rule removes them — grouped, so a built-in rule is never mistaken for the change being saved. */}
            {(preview.byRule?.length ?? 0) > 0 && (
              <div data-testid={`${testId}-by-rule`} className="flex flex-col gap-1">
                <div className="themed-text-muted">{k('byRule')}</div>
                <ul className="flex flex-col gap-0.5">
                  {preview.byRule!.map((r) => (
                    <li key={`${r.rule}:${r.folderRule ?? ''}`} className="flex items-baseline justify-between gap-2">
                      <span className="themed-text break-all min-w-0">{ruleLabel(r)}</span>
                      <span className="shrink-0 tabular-nums themed-text-muted">
                        {k('files', { count: r.files })} · {formatBytes(r.bytes)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {preview.samples.length > 0 && (
              <ul className="flex flex-col gap-2">
                {preview.samples.map((sm) => {
                  // the NAME on its own line (a truncating single line eats it first), the folder above it
                  const cut = sm.relPath.lastIndexOf('/')
                  const dir = cut === -1 ? '' : sm.relPath.slice(0, cut)
                  const name = cut === -1 ? sm.relPath : sm.relPath.slice(cut + 1)
                  const from = sm.before ?? sm.versionCount ?? 0
                  const to = sm.after ?? sm.keep ?? 0
                  return (
                    <li key={sm.relPath || `${from}-${to}`} className="flex flex-col gap-0.5 min-w-0 themed-bg-secondary rounded px-2 py-1.5">
                      {dir && <span className="font-mono text-[10px] themed-text-muted break-all leading-snug">{dir}/</span>}
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-mono themed-text break-all min-w-0">{name}</span>
                        <span className="shrink-0 tabular-nums themed-text-muted">{k('sample', { from, to })}</span>
                      </div>
                      {sm.rule !== undefined && (
                        <span className="text-[10px] themed-text-muted">{ruleLabel({ rule: sm.rule ?? null, folderRule: sm.folderRule, builtIn: sm.builtIn })}</span>
                      )}
                    </li>
                  )
                })}
                {preview.files > preview.samples.length && (
                  <li className="themed-text-muted px-2">{k('more', { count: preview.files - preview.samples.length })}</li>
                )}
              </ul>
            )}
            {/* Nothing the viewer may see named (#1458: the org preview names only files they can open) — the files
                still count above; say so rather than list nothing. */}
            {preview.samples.length === 0 && preview.files > 0 && (
              <p className="themed-text-muted" data-testid={`${testId}-unlisted`}>{k('unlisted', { count: preview.files })}</p>
            )}
          </>
        )}
        <p className="text-amber-400">{k('irreversible')}</p>
      </div>
    </Modal>
  )
}

/** What a Save that purged reports — the real deletes, or the background job it went to. */
export interface RetentionPurgeOutcomeView {
  purged?: { files: number; versions: number; appVersions?: number }
  queued?: { jobId: string }
}

/**
 * Preview, then confirm, then Save (#1443) — the ONE flow every retention-lowering Save uses. `run(preview, save)` asks
 * the preview; when nothing would be deleted it saves straight away (`save(false)`), otherwise it shows the dialog and
 * saves with `purge: true` only on confirm. Cancel saves NOTHING and resolves `null`. A preview that fails falls back
 * to the non-destructive save (the user keeps their setting; the next lowering offers the purge again).
 */
export function useRetentionPurgeConfirm(opts?: { testId?: string; formatBytes?: (n: number) => string }) {
  const [pending, setPending] = useState<{ preview: RetentionPurgePreviewView; confirm: () => void; cancel: () => void } | null>(null)
  const [busy, setBusy] = useState(false)
  // Bumped by `reset`: a preview still loading when its subject changed never opens a dialog afterwards.
  const generation = useRef(0)
  const pendingRef = useRef(pending)
  pendingRef.current = pending
  const busyRef = useRef(busy)
  busyRef.current = busy
  const run = useCallback(async <R,>(
    preview: () => Promise<RetentionPurgePreviewView>,
    save: (purge: boolean) => Promise<R>,
  ): Promise<R | null> => {
    const gen = generation.current
    let p: RetentionPurgePreviewView | null = null
    try { p = await preview() } catch { p = null }
    if (gen !== generation.current) return null // its file / app changed while the preview loaded: nothing saved
    // a save that finishes after its subject changed resolves null: the caller's continuation (refetch, receipt) is
    // for the old file / app and must not run in the new one's panel
    const current = (r: R): R | null => (gen === generation.current ? r : null)
    // a failure after the subject changed is not shown in the new panel either
    const failed = (err: unknown): R | null => { if (gen === generation.current) throw err; return null }
    if (!p?.willPurge) return save(false).then(current, failed)
    const shown = p
    return new Promise<R | null>((resolve, reject) => {
      const mine = {
        preview: shown,
        confirm: () => {
          setBusy(true)
          save(true).then((r) => resolve(current(r)), (err) => {
            try { resolve(failed(err)) } catch (e) { reject(e) }
          }).finally(() => {
            // only THIS run's dialog and busy flag — never a newer run's opened since
            setPending((cur) => (cur === mine ? null : cur))
            setBusy((b) => (gen === generation.current ? false : b))
          })
        },
        cancel: () => { setPending((cur) => (cur === mine ? null : cur)); resolve(null) },
      }
      setPending(mine)
    })
  }, [])
  /** Drop an open or loading dialog unanswered (its subject changed under it): nothing is saved, the pending `run`
   *  resolves null. A confirmed save already running is left to finish. */
  const reset = useCallback(() => {
    generation.current++ // a save still running finishes, but its result is no longer handed back
    if (busyRef.current) setBusy(false) // the new subject is not blocked by the old one's save
    pendingRef.current?.cancel()
  }, [])
  const dialog = pending ? (
    <RetentionPurgeDialog
      preview={pending.preview}
      busy={busy}
      onCancel={pending.cancel}
      onConfirm={pending.confirm}
      testId={opts?.testId}
      formatBytes={opts?.formatBytes}
    />
  ) : null
  return { run, dialog, reset }
}

/** The receipt under a Save: what was ACTUALLY removed, or that it is running in the background. */
export function RetentionPurgeReceipt({ outcome, testId = 'retention-purge-receipt' }: { outcome: RetentionPurgeOutcomeView | null; testId?: string }) {
  const { t } = useTranslation('core-ui')
  if (!outcome || (!outcome.purged && !outcome.queued)) return null
  const k = (key: string, o?: Record<string, unknown>): string => String(t(`settings.folderRules.purge.${key}`, (o ?? {}) as Record<string, unknown>))
  if (outcome.queued) return <p data-testid={testId} className="text-xs themed-text-muted">{k('queued')}</p>
  const p = outcome.purged!
  return (
    <p data-testid={testId} className="text-xs themed-text-muted">
      {p.versions === 0 && !p.appVersions ? k('doneNone') : null}
      {p.versions > 0 ? k('done', { versions: p.versions, count: p.versions, fileLabel: k('files', { count: p.files }) }) : null}
      {p.appVersions ? `${p.versions > 0 ? ' ' : ''}${k('doneAppVersions', { count: p.appVersions })}` : ''}
    </p>
  )
}
